/**
 * Dossier management module
 */
import {
  loadDossier,
  fetchProduct,
  createDossier as apiCreateDossier,
  addProductToDossier as apiAddProduct,
  updateDossierProductQuantity,
  removeProductFromDossier as apiRemoveProduct,
  updateDossierReference as apiUpdateReference
} from '../services/api.js';
import { handleApiError } from '../services/errorHandler.js';
import { log } from '../utils/log.js';
import { getClientId, getDossierId, setDossierId } from '../state.js';
import { ERROR_MESSAGES } from '../constants.js';
import { showAlert, updateGlobalTotal } from './ui.js';
import { addDossierItemToCard, updateCardWithProductDetails } from './product.js';

/**
 * DOM element references (initialized on setup)
 */
let dossierInputContainer = null;
let dossierNumberInput = null;
let dossierDisplayContainer = null;
let dossierDisplay = null;
let scannedItemsContainer = null;
let dossierReferenceInput = null;
let saveReferenceBtn = null;
let referenceSavedIndicator = null;
let endSessionBtn = null;

/**
 * Initializes the dossier module with DOM references
 * @param {object} elements - DOM element references
 */
export function initDossier(elements) {
  dossierInputContainer = elements.dossierInputContainer;
  dossierNumberInput = elements.dossierNumberInput;
  dossierDisplayContainer = elements.dossierDisplayContainer;
  dossierDisplay = elements.dossierDisplay;
  scannedItemsContainer = elements.scannedItemsContainer || document.getElementById('scanned-items-container');

  // Reference elements
  dossierReferenceInput = document.getElementById('dossier-reference');
  saveReferenceBtn = document.getElementById('save-reference-btn');
  referenceSavedIndicator = document.getElementById('reference-saved-indicator');
  endSessionBtn = document.getElementById('end-session-btn');

  // Set up reference save handler
  saveReferenceBtn?.addEventListener('click', handleSaveReference);
  dossierReferenceInput?.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveReference();
    }
  });
}

/**
 * Shows the dossier display with ID
 * @param {string} dossierId - Dossier ID to display
 * @param {string} reference - Optional dossier reference
 */
export function showDossierDisplay(dossierId, reference = '') {
  if (dossierDisplay) {
    dossierDisplay.textContent = `Numéro dossier : ${dossierId}`;
  }

  if (dossierInputContainer) {
    dossierInputContainer.hidden = true;
  }

  if (dossierDisplayContainer) {
    dossierDisplayContainer.hidden = false;
  }

  // Set reference if available
  if (dossierReferenceInput) {
    dossierReferenceInput.value = reference || '';
  }

  // Hide saved indicator when showing display
  if (referenceSavedIndicator) {
    referenceSavedIndicator.hidden = true;
  }

  // Show bottom end session button
  if (endSessionBtn) {
    endSessionBtn.hidden = false;
  }
}

/**
 * Shows the dossier input form
 */
export function showDossierInput() {
  if (dossierNumberInput) {
    dossierNumberInput.value = '';
  }

  if (dossierInputContainer) {
    dossierInputContainer.hidden = false;
  }

  if (dossierDisplayContainer) {
    dossierDisplayContainer.hidden = true;
  }

  // Reset reference field
  if (dossierReferenceInput) {
    dossierReferenceInput.value = '';
  }

  if (referenceSavedIndicator) {
    referenceSavedIndicator.hidden = true;
  }

  // Hide bottom end session button
  if (endSessionBtn) {
    endSessionBtn.hidden = true;
  }
}

/**
 * Loads products from a dossier
 * @param {string} dossierId - Dossier ID
 */
export async function loadDossierProducts(dossierId) {
  if (!dossierId) return;

  log.info(`Loading products for dossier ID: ${dossierId}`);

  try {
    const dossierData = await loadDossier(dossierId);
    const clientId = getClientId();

    // Check client match (convert both to string for comparison)
    if (clientId && String(dossierData.client_id) !== String(clientId)) {
      showAlert(
        `Le dossier #${dossierId} est associé à un autre client. La session du dossier a été réinitialisée.`
      );
      resetDossier();
      return;
    }

    // Clear existing cards
    const container = scannedItemsContainer || document.getElementById('scanned-items-container');
    if (container) {
      container.innerHTML = '';
    }

    // Add items to UI with product details
    if (dossierData.items) {
      for (const item of dossierData.items) {
        // Fetch product details to get option_reference
        let productDetails = null;
        try {
          productDetails = await fetchProduct(item.option_id);
        } catch {
          // Product details not available, continue with item data only
          log.info(`Could not fetch details for product ${item.option_id}`);
        }
        addDossierItemToCard(item, productDetails);
      }
    }

    updateGlobalTotal();
    setDossierId(dossierId);
    showDossierDisplay(dossierId, dossierData.dossier_reference || '');

    log.info(`Successfully loaded ${dossierData.items?.length || 0} products from dossier.`);
  } catch (error) {
    handleApiError(error, 'Load Dossier');
    showAlert(
      `Impossible de charger les produits du dossier ${dossierId}. Le dossier est peut-être vide ou a été supprimé.`
    );

    if (getDossierId() === dossierId) {
      resetDossier();
    }
  }
}

/**
 * Creates a new dossier with the first product
 * @param {HTMLElement} productCard - Product card element
 * @returns {Promise<string|null>} Dossier ID or null on failure
 */
export async function createDossier(productCard) {
  const clientId = getClientId();

  if (!clientId) {
    showAlert(ERROR_MESSAGES.VALIDATION_REQUIRED);
    return null;
  }

  const products = [
    {
      product_id: parseInt(productCard.dataset.productId, 10),
      quantity: parseInt(productCard.querySelector('.quantity-input').value, 10)
    }
  ];

  try {
    const result = await apiCreateDossier(clientId, products);

    log.info('Dossier created successfully:', result);

    // Update card with dossier product ID
    if (result.products && result.products.length > 0) {
      productCard.setAttribute('data-dossier-produit-id', result.products[0].dossier_produit_id);
    }

    return result.dossier_id;
  } catch (error) {
    handleApiError(error, 'Create Dossier');
    return null;
  }
}

/**
 * Adds a product to an existing dossier
 * @param {HTMLElement} card - Product card element
 */
export async function addProductToDossier(card) {
  const dossierId = getDossierId();
  const clientId = getClientId();

  if (!dossierId || !clientId) {
    showAlert('Dossier ou client non valide.');
    return;
  }

  try {
    const result = await apiAddProduct(
      dossierId,
      clientId,
      card.dataset.productId,
      card.querySelector('.quantity-input').value
    );

    // Update card with detailed product information
    updateCardWithProductDetails(card, result.product);

    log.info('Product added to dossier:', result);
  } catch (error) {
    handleApiError(error, 'Add Product to Dossier');
  }
}

/**
 * Updates product quantity in dossier
 * @param {HTMLElement} card - Product card element
 */
export async function updateProductQuantity(card) {
  let dossierProduitId = card.dataset.dossierProduitId;

  if (!dossierProduitId) {
    // Try to get from parent element
    const parentCard = card.closest('[data-dossier-produit-id]');
    if (parentCard) {
      dossierProduitId = parentCard.dataset.dossierProduitId;
      card.dataset.dossierProduitId = dossierProduitId;
    }
  }

  if (!dossierProduitId) {
    showAlert(ERROR_MESSAGES.MISSING_DOSSIER_PRODUCT_ID + ' Veuillez réessayer.');
    return;
  }

  try {
    await updateDossierProductQuantity(
      dossierProduitId,
      card.querySelector('.quantity-input').value
    );

    log.info('Product quantity updated successfully.');
  } catch (error) {
    handleApiError(error, 'Update Quantity');
  }
}

/**
 * Removes a product from dossier
 * @param {HTMLElement} card - Product card element
 */
export async function removeProductFromDossier(card) {
  const dossierProduitId = card.dataset.dossierProduitId;

  if (!dossierProduitId) {
    showAlert(ERROR_MESSAGES.MISSING_DOSSIER_PRODUCT_ID);
    return;
  }

  if (!confirm(ERROR_MESSAGES.CONFIRM_DELETE_PRODUCT)) {
    return;
  }

  try {
    await apiRemoveProduct(dossierProduitId);

    log.info('Product removed successfully.');
    card.remove();
    updateGlobalTotal();
  } catch (error) {
    handleApiError(error, 'Remove Product');
  }
}

/**
 * Handles automatic dossier creation when first product is scanned
 */
export async function handleDossierCreation() {
  let dossierId = getDossierId();

  if (!dossierId) {
    const container = scannedItemsContainer || document.getElementById('scanned-items-container');
    const firstCard = container?.querySelector('.card')?.parentElement;

    if (!firstCard) return;

    dossierId = await createDossier(firstCard);

    if (dossierId) {
      setDossierId(dossierId);
      showDossierDisplay(dossierId);
    }
  }
}

/**
 * Resets the dossier state
 */
export function resetDossier() {
  setDossierId(null);
  showDossierInput();

  const container = scannedItemsContainer || document.getElementById('scanned-items-container');
  if (container) {
    container.innerHTML = '';
  }

  updateGlobalTotal();
  log.info('Dossier has been reset.');
}

/**
 * Handles dossier validation from input
 */
export async function handleValidateDossier() {
  const dossierId = dossierNumberInput?.value;

  if (!dossierId) {
    showAlert(ERROR_MESSAGES.EMPTY_DOSSIER_ID);
    return;
  }

  await loadDossierProducts(dossierId);
}

/**
 * Handles edit dossier button click
 */
export function handleEditDossier() {
  if (confirm(ERROR_MESSAGES.CONFIRM_EDIT_DOSSIER)) {
    resetDossier();
  }
}

/**
 * Handles end session button click
 */
export function handleEndSession() {
  if (confirm(ERROR_MESSAGES.CONFIRM_END_SESSION)) {
    resetDossier();
  }
}

/**
 * Handles saving the dossier reference
 */
export async function handleSaveReference() {
  const dossierId = getDossierId();
  const reference = dossierReferenceInput?.value?.trim() || '';

  if (!dossierId) {
    showAlert('Aucun dossier actif. Veuillez d\'abord scanner un produit.');
    return;
  }

  // Disable button while saving
  if (saveReferenceBtn) {
    saveReferenceBtn.disabled = true;
    saveReferenceBtn.textContent = '...';
  }

  try {
    await apiUpdateReference(dossierId, reference);

    log.info(`Reference updated for dossier ${dossierId}: ${reference}`);

    // Show saved indicator
    if (referenceSavedIndicator) {
      referenceSavedIndicator.hidden = false;
      // Auto-hide after 3 seconds
      setTimeout(() => {
        if (referenceSavedIndicator) {
          referenceSavedIndicator.hidden = true;
        }
      }, 3000);
    }
  } catch (error) {
    handleApiError(error, 'Update Reference');
  } finally {
    // Re-enable button
    if (saveReferenceBtn) {
      saveReferenceBtn.disabled = false;
      saveReferenceBtn.textContent = 'Enregistrer';
    }
  }
}
